/**
 * Reading a Sistemas page out of content/ops/sistemas/*.md.
 *
 * A page carries both languages in one file: the Spanish half, a line with
 * three dashes, then the English half. Each half has short sections, and the
 * two the crew needs are the numbered "what you do" list and the "if it fails"
 * line. Nothing here invents text: a page with no numbered list is reported as
 * not recorded yet, and the screen says so honestly.
 */

export type Half = { es: string; en: string };

/** Splits the body at the first line that is only three dashes. */
export function splitLanguages(body: string): Half {
  const lines = body.split(/\r?\n/);
  const cut = lines.findIndex((line) => /^-{3,}\s*$/.test(line));
  if (cut === -1) return { es: body.trim(), en: body.trim() };
  return {
    es: lines.slice(0, cut).join('\n').trim(),
    en: lines.slice(cut + 1).join('\n').trim(),
  };
}

export type SystemsPage = {
  /** The short "what is this" paragraph. Empty when the page has none. */
  intro: string;
  /** The numbered do-this list, without the numbers. */
  steps: string[];
  /** The "if it fails, do this first, then call" text. Empty when not written. */
  ifItFails: string;
  /** True only when there is a real numbered list to follow. */
  recorded: boolean;
};

const FAIL_HEADING = /^(si falla|if it fails|si algo falla)\b/i;

export function parseHalf(markdown: string): SystemsPage {
  const sections: { heading: string; lines: string[] }[] = [{ heading: '', lines: [] }];
  for (const line of markdown.split(/\r?\n/)) {
    const heading = /^##\s+(.+?)\s*$/.exec(line);
    if (heading) sections.push({ heading: heading[1] ?? '', lines: [] });
    else sections[sections.length - 1]?.lines.push(line);
  }

  let intro = '';
  let steps: string[] = [];
  let ifItFails = '';

  for (const section of sections) {
    const text = section.lines.join('\n').trim();
    if (FAIL_HEADING.test(section.heading)) {
      ifItFails = text;
      continue;
    }
    const numbered = section.lines
      .map((line) => /^\s*\d+[.)]\s+(.+)$/.exec(line)?.[1]?.trim())
      .filter((line): line is string => Boolean(line));
    if (numbered.length > 0 && steps.length === 0) {
      steps = numbered;
    } else if (!intro && text && numbered.length === 0) {
      intro = text.replace(/\s*\n\s*/g, ' ');
    }
  }

  return { intro, steps, ifItFails, recorded: steps.length > 0 };
}

export function parseSystemsBody(body: string): { es: SystemsPage; en: SystemsPage } {
  const half = splitLanguages(body);
  return { es: parseHalf(half.es), en: parseHalf(half.en) };
}

/** The page that documents an asset, matched by the `asset:` line in its front matter. */
export function docForAsset<T extends { meta: Record<string, unknown> }>(
  docs: T[],
  assetSlug: string,
): T | undefined {
  return docs.find((doc) => doc.meta.asset === assetSlug);
}
